import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, AnimationGeneratorType } from 'motion/react';
import * as motion from 'motion/react-m';
import Image from 'next/image';
import { faHeadSideBrain } from '@fortawesome/pro-solid-svg-icons/faHeadSideBrain';
import { faHexagonCheck } from '@fortawesome/pro-solid-svg-icons/faHexagonCheck';
import { faVideo } from '@fortawesome/pro-solid-svg-icons/faVideo';
import { faAtomSimple } from '@fortawesome/pro-solid-svg-icons/faAtomSimple';
import { faGaugeMax } from '@fortawesome/pro-regular-svg-icons/faGaugeMax';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';

import themeSchema, { getTextColor } from '../../../lib/composer/schemas/theme';
import {
  text0,
  text5,
  textColorClassMap,
} from '@osmosis/tailwind-ui/styles/text';
import { REBASS_TO_TAILWIND_BG_MAP } from '@osmosis/tailwind-ui/styles/mapping';
import { H3, H4, P } from '@osmosis/tailwind-ui/components/Text';
import { Flex, Box } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { TabContent, TabNavigationBlockProps } from '../../../types/composer';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { tabsSchema } from '../../../lib/composer/schemas/tabNavigation';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { bg0, bgPrimaryText } from '@osmosis/tailwind-ui/styles';

type TabBarProps = {
  tabs: TabNavigationBlockProps['tabs'];
  activeTab: string | undefined;
  onTabClick: (tabName: string) => void;
  tabRefs: React.MutableRefObject<Record<string, HTMLButtonElement | null>>;
  theme?: string;
};
type TabContentProps = {
  activeTab: string | undefined;
  content?: TabContent;
  animationDirection?: number;
};

const iconsMap: Record<string, IconDefinition> = {
  'gauge-max': faGaugeMax,
  video: faVideo,
  'hexagon-check': faHexagonCheck,
  'atom-simple': faAtomSimple,
  'head-side-brain': faHeadSideBrain,
};

const sectionVariants = {
  initial: (direction: number) => ({
    x: direction > 0 ? '100%' : direction < 0 ? '-100%' : 0,
    opacity: 1,
  }),
  animate: {
    x: 0,
    opacity: 1,
    transition: {
      x: {
        type: 'spring' as AnimationGeneratorType,
        stiffness: 300,
        damping: 30,
      },
      opacity: { duration: 0 },
    },
  },
  exit: (direction: number) => ({
    x: direction > 0 ? '-100%' : direction < 0 ? '100%' : 0,
    opacity: 1,
    transition: {
      x: {
        type: 'spring' as AnimationGeneratorType,
        stiffness: 300,
        damping: 30,
      },
      opacity: { duration: 0 },
    },
  }),
};

const TabBar = ({
  tabs,
  activeTab,
  onTabClick,
  tabRefs,
  theme = '',
}: TabBarProps) => {
  const textColor: string | undefined = getTextColor(theme);
  const textColorClass = textColorClassMap[textColor] || text5;

  return (
    <Flex
      className="relative mx-auto items-center justify-around gap-2 px-8"
      role="tablist"
    >
      {tabs.map((tab, idx) => {
        const tabId = tab.tabName.replace(/\s+/g, '-') + '-tab';
        const panelId = tab.tabName.replace(/\s+/g, '-') + '-panel';
        return (
          <Button
            key={`${tab.tabName}-${idx}`}
            data-testid={`tab-${tab.tabName}`}
            id={tabId}
            role="tab"
            variant="transparent"
            aria-selected={activeTab === tab.tabName}
            aria-controls={panelId}
            ref={(el: HTMLButtonElement | null) => { tabRefs.current[tab.tabName] = el; }}
            className={`group relative flex-shrink-0 px-4 py-2 hover:bg-transparent focus:outline-none focus-visible:rounded-b-none focus-visible:ring-1 focus-visible:ring-black active:bg-transparent`}
            onClick={() => onTabClick(tab.tabName)}
          >
            <H4 className={textColorClass}>{tab.tabName}</H4>
            <span
              className={cn(
                `absolute bottom-0 left-0 z-10 h-1 w-full group-hover:block group-focus-visible:block`,
                activeTab === tab.tabName ? 'block' : 'hidden',
                bgPrimaryText,
              )}
              aria-hidden
            />
          </Button>
        );
      })}
    </Flex>
  );
};

const ContentBlock = ({
  content,
  activeTab,
  animationDirection,
}: TabContentProps) => {
  const [contentHeight, setContentHeight] = useState('auto');
  const contentRef = useRef<HTMLDivElement | null>(null);

  const onChangeContent = () => {
    if (contentRef.current) {
      setContentHeight(`${contentRef.current.offsetHeight}px`);
    }
  };

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(`${contentRef.current.offsetHeight}px`);
    }
  }, [activeTab, content]);

  useEffect(() => {
    window.addEventListener('resize', onChangeContent);

    return () => {
      window.removeEventListener('resize', onChangeContent);
    };
  }, []);

  if (!content) {
    return null;
  }

  const { illustrationPosition, mainDefinition, features, image } = content;
  const illustrationIsLeft = illustrationPosition === 'left';

  return (
    <Flex
      className="relative m-auto min-h-[600px] max-w-none items-center justify-center overflow-hidden xl:max-w-[1392px]"
      aria-labelledby="section-title"
      style={{ height: contentHeight }}
    >
      <AnimatePresence custom={animationDirection}>
        <motion.div
          key={activeTab}
          id={activeTab?.replace(/\s+/g, '-') + '-panel'}
          role="tabpanel"
          aria-labelledby={activeTab?.replace(/\s+/g, '-') + '-tab'}
          variants={sectionVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          custom={animationDirection}
          className="absolute left-0 top-0 w-full px-4"
        >
          <Flex
            className={`flex-col items-center  justify-between py-10 md:py-20
                          ${
                            illustrationIsLeft
                              ? 'lg:flex-row-reverse'
                              : 'lg:flex-row'
                          }`}
            ref={contentRef}
          >
            <Box
              className={`mt mb-12 w-full max-w-[578px] lg:mb-0 lg:w-[46%] lg:pl-0 ${
                illustrationIsLeft ? '' : 'mr-4'
              }`}
            >
              {mainDefinition.title ? (
                <Box
                  className={cn(
                    'mb-8 rounded-[48px]  py-8 pl-[23px] pr-2.5',
                    bgPrimaryText,
                    text0,
                  )}
                >
                  <Flex className="mb-4 flex items-center">
                    {iconsMap[mainDefinition.icon] ? (
                      <Icon
                        icon={iconsMap[mainDefinition.icon]}
                        size={'2xl'}
                        className="mr-3"
                      />
                    ) : null}
                    <H3
                      className={cn(
                        'xl:text-8 text-[26px] font-bold sm:text-3xl md:text-3xl',
                        text0,
                      )}
                    >
                      {mainDefinition.title}
                    </H3>
                  </Flex>
                  {mainDefinition.description ? (
                    <P className="text-lg sm:text-2xl">
                      {mainDefinition.description}
                    </P>
                  ) : null}
                </Box>
              ) : null}

              <ul className="grid grid-cols-1 gap-6">
                {features?.map((feature) => (
                  <li
                    key={feature.text}
                    className={cn('flex items-center rounded-xl p-0', bg0)}
                  >
                    {iconsMap[feature.icon] ? (
                      <Box className="mr-4 mt-1 flex-shrink-0">
                        <Icon icon={iconsMap[feature.icon]} size={'2xl'} />
                      </Box>
                    ) : null}
                    <P className={cn('text-lg sm:text-2xl', text5)}>
                      {feature.text}
                    </P>
                  </li>
                ))}
              </ul>
            </Box>
            {image?.image ? (
              <Flex
                className={`relative w-full items-center justify-center lg:w-[54%] ${
                  illustrationIsLeft ? 'mr-4' : ''
                }`}
              >
                <Box className="relative aspect-[1.5/1] w-full items-center justify-center overflow-hidden">
                  <Image
                    src={image.image}
                    alt={`Illustration depicting concepts related to ${activeTab}`}
                    fill
                    className="h-auto w-full object-contain"
                  />
                </Box>
              </Flex>
            ) : null}
          </Flex>
        </motion.div>
      </AnimatePresence>
    </Flex>
  );
};

/**
 * TabNavigation component.
 */
const TabNavigation = ({ tabs = [], theme = '' }: TabNavigationBlockProps) => {
  const [activeTab, setActiveTab] = useState<string | undefined>(
    tabs[0]?.tabName,
  );
  const [direction, setDirection] = useState(0);
  const [trianglePosition, setTrianglePosition] = useState({
    left: 0,
  });

  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const navBarRef = useRef<HTMLDivElement | null>(null);

  const currentContent = useMemo(
    () => tabs.find((tab) => tab.tabName === activeTab)?.tabContent,
    [activeTab, tabs],
  );

  useEffect(() => {
    if (!tabs[0]?.tabName) return;

    if (!activeTab || !tabs.some((tab) => tab.tabName === activeTab)) {
      setActiveTab(tabs.find((tab) => tab.tabName)?.tabName);
    }
  }, [activeTab, tabs]);

  useEffect(() => {
    if (activeTab && tabRefs.current[activeTab]) {
      const activeTabElement = tabRefs.current[activeTab];
      if (navBarRef.current) {
        const navRect = navBarRef.current.getBoundingClientRect();
        const tabRect = activeTabElement.getBoundingClientRect();

        const left = (tabRect.left + tabRect.right) / 2 - navRect.left;

        setTrianglePosition({ left: left });
      }
    }
  }, [activeTab]);

  const handleTabClick = (tabName: string) => {
    const oldIndex = tabs.findIndex((tab) => tab.tabName === activeTab);
    const newIndex = tabs.findIndex((tab) => tab.tabName === tabName);
    if (newIndex > oldIndex) {
      setDirection(1);
    } else if (newIndex < oldIndex) {
      setDirection(-1);
    } else {
      setDirection(0);
    }
    setActiveTab(tabName);
  };

  const tabBarBG = REBASS_TO_TAILWIND_BG_MAP[theme] || 'bg-transparent';

  return (
    <Box className={cn(bg0)}>
      <Box
        className={`relative w-full overflow-x-auto overflow-y-hidden py-8 ${tabBarBG}`}
        ref={navBarRef}
      >
        <TabBar
          tabs={tabs}
          activeTab={activeTab}
          onTabClick={handleTabClick}
          tabRefs={tabRefs}
          theme={theme}
        />
        <Box
          className="absolute -bottom-[1px] h-0 w-0 border-b-8 border-l-8 border-r-8 border-b-white border-l-transparent border-r-transparent transition-all duration-300"
          style={{
            left: `${trianglePosition.left}px`,
            transform: 'translateX(-50%)',
          }}
          aria-hidden
        />
      </Box>
      <ContentBlock
        content={currentContent}
        animationDirection={direction}
        activeTab={activeTab}
      />
    </Box>
  );
};

export default TabNavigation;

TabNavigation.schema = {
  name: 'tab-navigation',
  label: 'Tab Navigation',
  editableProps: [tabsSchema, themeSchema],
};
